import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";

const WizardSelectedExercisesItemTemplate = ({ exercise, index, handleDelete, isEditModeActive, setExerciseVideoDialogVisible, setIsCollapsedDetails, setSelectedExercise, setSelectedExerciseForVideo, moveCard, t }: any) => {
    const handleClick = () => {
        if (isEditModeActive) return;
        setSelectedExerciseForVideo(exercise?.exercise);
        setExerciseVideoDialogVisible(true);
    }

    const handleDeleteOrEdit = (e: any) => {
        e.stopPropagation();
        if (!isEditModeActive) {
            handleDelete(exercise);
        } else {
            setSelectedExercise(exercise);
            setIsCollapsedDetails(true);
        }
    }

    const ref = useRef<HTMLDivElement>(null);
    
    const [{ handlerId }, drop] = useDrop({
        accept: "exercise",
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(item: any, monitor: any) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            if (dragIndex === hoverIndex) {
                return;
            }

            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            
            if (!clientOffset) {
                return;
            }
            
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            moveCard(dragIndex, hoverIndex);

            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag] = useDrag({
        type: "exercise",
        canDrag: () => !isEditModeActive,
        item: () => {
            return { id: exercise.exercise.id, index };
        },
        collect: (monitor: any) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const opacity = isDragging ? 0.4 : 1;
    drag(drop(ref));

    return (
        <div ref={ref} className={`col-12 flex align-items-center gap-2 relative ${!isEditModeActive ? "cursor-pointer" : ""}`} onClick={handleClick} style={{ opacity }}>
            {!isEditModeActive && <img src="/dragIconWhite.svg" style={{ cursor: "grab", width: '2rem' }} />}
            <div className="text-md font-bold" style={{ color: "#2D3967", fontFamily: "monospace" }}>
                {index + 1}.
            </div>
            <div className="flex flex-wrap w-full align-items-center justify-content-between bg-white px-3 h-auto min-h-7rem" style={{ borderRadius: "18px" }}>
                <Button
                    onClick={handleDeleteOrEdit}
                    className="absolute"
                    style={{ backgroundColor: isEditModeActive ? "#2D3967" : "#E3506B", border: "none", top: "-0.25rem", right: "0.1rem", width: "1.5rem", height: "1.5rem", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                    icon={isEditModeActive ? "pi pi-pencil" : "pi pi-times"}
                    rounded
                    aria-label={isEditModeActive ? "Edit" : "Delete"}
                />
                <div className="flex align-items-center w-full" style={{ maxWidth: '30%' }}>
                    <img
                        src={exercise?.exercise?.image}
                        alt={exercise?.exercise?.name}
                        className="flex-shrink-0"
                        style={{
                            width: "2.5rem",
                            height: "2.5rem",
                            objectFit: "contain",
                            borderRadius: "12px",
                            marginRight: "0.75rem"
                        }}
                    />
                    <Tooltip target=".selected-exercise-name-tooltip-span" />
                    <div className="flex-grow-1 flex-shrink-1 mr-2 w-full">
                        <p className="selected-exercise-name-tooltip-span text-md font-bold m-0 white-space-nowrap text-overflow-ellipsis overflow-hidden"
                                data-pr-tooltip={exercise?.exercise?.name} data-pr-position="bottom"
                            style={{ color: "#2D3967" }}>
                            {exercise?.exercise?.name}
                        </p>
                    </div>
                </div>
                <div className="flex gap-3 flex-grow-0 flex-shrink-1 text-sm">
                    <div className="flex gap-2">
                        <span className="text-400 font-light">{!exercise?.isEveryOtherDay ? t("days") : t("alternateDays")}</span>
                        {!exercise?.isEveryOtherDay ? (
                            <>
                                <Tooltip target=".selected-exercise-days-tooltip-span" />
                                <span className="selected-exercise-days-tooltip-span font-bold text-overflow-ellipsis overflow-hidden white-space-nowrap"
                                    style={{ color: "#2D3967", maxWidth: "100px" }}
                                    data-pr-tooltip={exercise?.days?.map((day: any) => t(day)).join(", ")} data-pr-position="bottom">
                                    {exercise?.days?.map((day: any) => t(day)).join(", ")}
                                </span>
                            </>
                        ) : (
                            <span className="font-bold text-overflow-ellipsis overflow-hidden white-space-nowrap"
                                style={{ color: "#2D3967", maxWidth: "100px" }}>
                                {exercise?.everyOtherDayPeriod}
                            </span>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <span className="text-400 font-light whitespace-nowrap">{t("tolerance")}</span>
                        <span className="font-bold text-overflow-ellipsis overflow-hidden"
                            style={{ color: "#2D3967" }}>
                            {exercise?.tolerance + "%"}
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <span className="text-400 font-light whitespace-nowrap">{t("set")}, {t("reps")}</span>
                        <span className="font-bold text-overflow-ellipsis overflow-hidden"
                            style={{ color: "#2D3967" }}>
                            {exercise?.set + " x " + exercise?.repeat}
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <span className="text-400 font-light whitespace-nowrap text-md">{t("breakTime")}</span>
                        <span className="text-overflow-ellipsis overflow-hidden"
                            style={{ color: "#2D3967" }}>
                            {exercise?.restInterval + " " + t("hours")}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default WizardSelectedExercisesItemTemplate;